import { UnifiedListLayout } from "@/components/layout/unified-list-layout";
import { IntroCard } from "@/components/shared/IntroCard";
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useProjectSettings } from "@/hooks/useProjectSettings";
import {
  useCreateThought,
  useDeleteThought,
  useProjectThoughts,
  useUpdateThought
} from '@/services/query/thoughts-queries';
import { Thought } from '@/services/supabase/thoughts';
import { AnimatePresence, motion } from 'framer-motion';
import { FileText, PlusCircle } from 'lucide-react';
import React, { useState } from 'react';
import { ThoughtCard } from './thought-card';
import { ThoughtEditorDialog } from './thought-editor-dialog';
import { ThoughtsToolbar } from './thoughts-toolbar';

interface ThoughtsBoardProps {
  projectId: string | number;
  children?: React.ReactNode;
}

export const ThoughtsBoard: React.FC<ThoughtsBoardProps> = ({ projectId }) => {
  const { toast } = useToast();

  // Settings for Intro Card
  const { useIntroDismissal } = useProjectSettings(projectId.toString());
  const { isDismissed: isIntroDismissed, dismiss: dismissIntro } = useIntroDismissal('thoughts');

  // State for thoughts and UI
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);
  const [currentThought, setCurrentThought] = useState<Thought | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [activeTab, setActiveTab] = useState<'all' | 'favorites'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Fetch thoughts data
  const {
    data: thoughts = [],
    isLoading,
    error
  } = useProjectThoughts(projectId);

  const showIntro = !isIntroDismissed && !isLoading && thoughts.length === 0;

  // Mutations
  const createThoughtMutation = useCreateThought();
  const updateThoughtMutation = useUpdateThought();
  const deleteThoughtMutation = useDeleteThought();

  // Filter and sort thoughts
  const filteredAndSortedThoughts = React.useMemo(() => {
    // First filter by active tab
    let filtered = activeTab === 'favorites'
      ? thoughts.filter(thought => thought.is_favorite)
      : [...thoughts];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(thought =>
        (thought.title?.toLowerCase().includes(query)) ||
        (thought.content?.toLowerCase().includes(query))
      );
    }

    // Then apply sorting
    const sorted = [...filtered]; // Create a copy before sorting
    switch (sortOrder) {
      case 'newest':
        return sorted.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case 'oldest':
        return sorted.sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      default:
        return sorted;
    }
  }, [thoughts, sortOrder, activeTab, searchQuery]);

  // Handle creating a new thought
  const handleCreateThought = () => {
    setCurrentThought(null);
    setIsCreating(true);
    setIsEditorOpen(true);
  };

  // Handle editing a thought
  const handleEditThought = (thought: Thought) => {
    setCurrentThought(thought);
    setIsCreating(false);
    setIsEditorOpen(true);
  };

  // Handle saving a thought (create or update)
  const handleSaveThought = async (thoughtData: Partial<Thought>) => {
    try {
      if (isCreating) {
        // Create new thought
        await createThoughtMutation.mutateAsync({
          projectId,
          title: thoughtData.title || 'Untitled',
          content: thoughtData.content || ''
        });
      } else if (currentThought) {
        // Update existing thought
        await updateThoughtMutation.mutateAsync({
          thoughtId: currentThought.id,
          updates: {
            title: thoughtData.title,
            content: thoughtData.content
          },
          projectId
        });
      }
    } catch (error) {
      console.error('Error saving thought:', error);
      toast({
        title: "Error",
        description: "Failed to save thought. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle toggling favorite status
  const handleToggleFavorite = async (thought: Thought) => {
    try {
      await updateThoughtMutation.mutateAsync({
        thoughtId: thought.id,
        updates: {
          is_favorite: !thought.is_favorite
        },
        projectId
      });

      toast({
        title: thought.is_favorite ? "Removed from Favorites" : "Added to Favorites",
        description: thought.is_favorite
          ? "The thought has been removed from favorites"
          : "The thought has been added to favorites",
      });
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      toast({
        title: "Error",
        description: "Failed to update favorite status. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle deleting a thought
  const handleDeleteThought = async (thought: Thought) => {
    try {
      await deleteThoughtMutation.mutateAsync({
        thoughtId: thought.id,
        projectId
      });

      toast({
        title: "Thought Deleted",
        description: "The thought has been deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting thought:', error);
      toast({
        title: "Error",
        description: "Failed to delete thought. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <UnifiedListLayout
        layoutMode="list"
        toolbar={
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full">
            <ThoughtsToolbar
              activeTab={activeTab}
              onTabChange={setActiveTab}
              sortOrder={sortOrder}
              onSortChange={setSortOrder}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              className="flex-1"
            />
            <Button
              onClick={handleCreateThought}
              className="h-10 px-6 rounded-full shadow-lg shadow-primary/20 flex-shrink-0"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              New Thought
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Intro Card */}
          {showIntro && (
            <IntroCard
              title="Capture Fleeting Ideas"
              description="Use the Thoughts board to quickly jot down ideas, character concepts, or plot fragments that don't fit anywhere else yet. Drag and drop to organize them visually."
              onClose={dismissIntro}
              className="mb-8"
              actions={
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">Idea Management</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Convert thoughts into formal Notes or Tasks when they are ready to be developed further.
                    </p>
                  </div>
                </div>
              }
            />
          )}

          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-muted-foreground text-sm">Loading thoughts...</p>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-destructive text-sm">Error loading thoughts. Please try again.</p>
            </div>
          ) : filteredAndSortedThoughts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 text-muted-foreground animate-in zoom-in-95 duration-300">
                <div className="h-24 w-24 bg-muted/20 rounded-3xl flex items-center justify-center mb-6 border border-border/50">
                  <FileText className="h-10 w-10 text-muted-foreground/30" />
                </div>
                <p className="text-xl font-medium text-foreground">
                  {searchQuery ? 'No thoughts found' : (activeTab === 'favorites' ? 'No favorite thoughts' : 'No thoughts yet')}
                </p>
                <p className="text-sm opacity-60 max-w-xs text-center mt-2 mb-8">
                  {searchQuery
                    ? `No thoughts matching "${searchQuery}"`
                    : (activeTab === 'favorites'
                      ? 'Mark thoughts as favorites to see them here'
                      : 'Capture your ideas, inspirations, and notes for your writing projects')
                  }
                </p>
                {!searchQuery && (
                  <Button
                    onClick={handleCreateThought}
                    className="rounded-full px-6 shadow-lg shadow-primary/20"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    {activeTab === 'favorites' ? 'Create New Thought' : 'Add Your First Thought'}
                  </Button>
                )}
              </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              <AnimatePresence mode="popLayout">
                {filteredAndSortedThoughts.map((thought, index) => (
                  <motion.div
                    key={thought.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    layout
                    className="h-full" // Ensure full height for grid items
                  >
                    <ThoughtCard
                      thought={thought}
                      isSelected={false}
                      onEdit={handleEditThought}
                      onDelete={handleDeleteThought}
                      onToggleFavorite={handleToggleFavorite}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </UnifiedListLayout>

      {/* Editor Dialog */}
      <ThoughtEditorDialog
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        onSave={handleSaveThought}
        thought={currentThought}
        isCreating={isCreating}
      />
    </>
  );
};
